import { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { formatTrimmedDecimal } from '@/helpers/finance';
import { StatCard, StatCardRow } from '@/modules/common/components/molecules/stat-card';

import { OverheadSummaryBucket } from '../../types/overhead';

const AMOUNT_DISPLAY_DECIMAL_PLACES = 2;

export interface OverheadsSummaryCardRow {
  label: string;
  bucket: OverheadSummaryBucket;
}

interface OverheadsSummaryCardProps {
  icon: ReactNode;
  title: string;
  infoTooltip: string;
  rows: OverheadsSummaryCardRow[];
  currencyCode: string;
}

/** "1250 USD / 3" — amount in the org currency, then entry count after a slash. */
export const formatOverheadAmount = (amount: number, currencyCode: string): string =>
  `${formatTrimmedDecimal(amount, AMOUNT_DISPLAY_DECIMAL_PLACES)} ${currencyCode}`;

const SummaryValue = ({
  bucket,
  currencyCode,
}: {
  bucket: OverheadSummaryBucket;
  currencyCode: string;
}) => {
  const { t } = useTranslation('authenticated.overheads');

  return (
    <span className="flex items-center gap-1 whitespace-nowrap text-sm font-semibold tabular-nums">
      <span>{formatOverheadAmount(bucket.amount, currencyCode)}</span>
      <span className="font-normal text-muted-foreground">
        {` / ${t('summary.entriesCount', { count: bucket.count })}`}
      </span>
    </span>
  );
};

/**
 * One summary card (Income or Cost): a row per status bucket plus a total row,
 * each formatted as "amount ORG_CURRENCY / entry count".
 */
export const OverheadsSummaryCard = ({
  icon,
  title,
  infoTooltip,
  rows,
  currencyCode,
}: OverheadsSummaryCardProps) => {
  const { t } = useTranslation('authenticated.overheads');

  const total: OverheadSummaryBucket = rows.reduce(
    (acc, row) => ({
      amount: acc.amount + row.bucket.amount,
      count: acc.count + row.bucket.count,
    }),
    { amount: 0, count: 0 }
  );

  return (
    <StatCard icon={icon} title={title} infoTooltip={infoTooltip}>
      {rows.map(row => (
        <StatCardRow
          key={row.label}
          label={row.label}
          value={<SummaryValue bucket={row.bucket} currencyCode={currencyCode} />}
        />
      ))}
      <StatCardRow
        label={t('summary.rows.total')}
        value={<SummaryValue bucket={total} currencyCode={currencyCode} />}
        className="border-t border-border pt-2"
      />
    </StatCard>
  );
};
